<template>
  <div
    class="min-h-screen relative overflow-hidden flex items-center justify-center font-NotoSerifKhmer p-4"
  >
    <!-- Animated Background -->
    <div
      class="fixed inset-0 bg-gradient-to-br from-indigo-500 via-purple-600 to-pink-500 bg-[length:400%_400%] animate-gradient -z-20"
    ></div>
    <div
      class="fixed inset-0 -z-10 bg-[radial-gradient(circle_at_20%_50%,rgba(255,107,157,0.3)_0%,transparent_50%),radial-gradient(circle_at_80%_80%,rgba(102,126,234,0.3)_0%,transparent_50%),radial-gradient(circle_at_40%_20%,rgba(67,233,123,0.2)_0%,transparent_50%)]"
    ></div>

    <!-- Floating Decorative Elements -->
    <div
      class="fixed top-20 left-10 w-72 h-72 bg-pink-400/20 rounded-full blur-3xl animate-pulse"
    ></div>
    <div
      class="fixed bottom-20 right-10 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl animate-pulse"
      style="animation-delay: 1s"
    ></div>

    <!-- Language Selector -->
    <div class="fixed top-6 right-6 z-50 px-4 py-2" ref="languageDropdown">
      <div class="relative">
        <button
          @click.stop="toggleLanguageMenu"
          class="flex items-center gap-3 px-3 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-xl rounded-2xl transition-all duration-300 border border-white/20 shadow-lg group"
        >
          <div class="flex items-center gap-2">
            <img
              :src="languages.find((l) => l.code === selectedLang)?.flag"
              class="w-5 h-3.5 rounded-sm object-cover shadow-sm"
            />
            <span class="text-white font-semibold text-sm hidden sm:block">{{
              languages.find((l) => l.code === selectedLang)?.name
            }}</span>
          </div>
          <svg
            class="w-4 h-4 text-white transition-transform duration-300"
            :class="{ 'rotate-180': showLanguageMenu }"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        <!-- Dropdown Menu -->
        <transition
          enter-active-class="transition ease-out duration-200"
          enter-from-class="opacity-0 scale-95 translate-y-2"
          enter-to-class="opacity-100 scale-100 translate-y-0"
          leave-active-class="transition ease-in duration-150"
          leave-from-class="opacity-100 scale-100 translate-y-0"
          leave-to-class="opacity-0 scale-95 translate-y-2"
        >
          <div
            v-if="showLanguageMenu"
            class="absolute right-0 mt-3 w-48 bg-white/15 backdrop-blur-2xl rounded-2xl border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.3)] overflow-hidden"
          >
            <div class="p-2 space-y-1">
              <button
                v-for="lang in languages"
                :key="lang.code"
                @click="onLanguageChange(lang.code)"
                class="w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all duration-200 group/item"
                :class="
                  selectedLang === lang.code
                    ? 'bg-white/20 text-white'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                "
              >
                <div class="flex items-center gap-3">
                  <img
                    :src="lang.flag"
                    :alt="lang.name"
                    class="w-5 h-3.5 rounded-sm object-cover shadow-sm"
                  />
                  <span class="text-sm font-medium">{{ lang.name }}</span>
                </div>
                <div
                  v-if="selectedLang === lang.code"
                  class="w-1.5 h-1.5 rounded-full bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.5)]"
                ></div>
              </button>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- Forgot Password Card -->
    <div class="w-full max-w-md animate-fade-in-up">
      <!-- Logo/Header -->
      <div class="text-center mb-8">
        <div
          class="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-xl rounded-3xl border border-white/30 shadow-[0_8px_32px_rgba(0,0,0,0.1)] mb-4 animate-bounce-slow"
        >
          <span class="text-5xl">🔐</span>
        </div>
        <h1
          class="text-4xl md:text-5xl font-bold text-white mb-2 drop-shadow-lg"
        >
          {{ $t("Forgot Password") }}
        </h1>
        <p class="text-white/80 text-lg">
          {{ $t("Enter your email address and we'll send you a link to reset your password.") }}
        </p>
      </div>

      <!-- Form Card -->
      <div
        class="bg-white/15 backdrop-blur-xl rounded-3xl border border-white/30 shadow-[0_8px_32px_rgba(0,0,0,0.1),0_2px_8px_rgba(255,255,255,0.2)_inset] p-8 transition-all duration-300 hover:shadow-[0_12px_48px_rgba(0,0,0,0.15),0_2px_12px_rgba(255,255,255,0.3)_inset]"
      >
        <form @submit.prevent="handleReset" class="space-y-6">
          <!-- Email Input -->
          <div>
            <label
              for="email"
              class="block text-sm font-semibold text-white/90 mb-2"
            >
              {{ $t("Email Address") }}
            </label>
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"
              >
                <svg
                  class="w-5 h-5 text-white/60"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                  />
                </svg>
              </div>
              <input
                id="email"
                v-model="email"
                type="email"
                required
                :placeholder="$t('Enter your email')"
                class="w-full bg-white/10 border border-white/20 rounded-2xl py-3.5 pl-12 pr-4 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40 transition-all duration-300"
              />
            </div>
          </div>
          
          <div class="" v-if="error">
            <span class="text-red-500">{{ error }}</span>
          </div>
          <div class="" v-if="success">
            <span class="text-green-400">{{ success }}</span>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="isPending"
            class="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold py-4 rounded-2xl shadow-[0_8px_24px_rgba(168,85,247,0.4)] hover:shadow-[0_12px_32px_rgba(168,85,247,0.5)] transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            <span
              v-if="!isPending"
              class="flex items-center justify-center gap-2"
            >
              <span>{{ $t("Send Reset Link") }}</span>
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </span>
            <span v-else class="flex items-center justify-center gap-2">
              <svg class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span>{{ $t("Sending...") }}</span>
            </span>
          </button>
        </form>
      </div>

      <!-- Back to Sign In Link -->
      <div class="text-center mt-6">
        <div class="text-white/80 flex space-x-2 justify-center">
          <span>{{ $t("Remember your password?") }}</span>
          <div
            @click="router.push('/login')"
            class="text-white font-bold hover:underline ml-1 cursor-pointer"
          >
            {{ $t("Back to Sign In") }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import khFlag from "@/assets/Cambodia.png";
import usaFlag from "@/assets/English.png";
import { getCollectionQuery } from "@/composables/getCollection";
import { where } from "firebase/firestore";
import useResetPassword from "@/composables/useResetPassword";

const router = useRouter();
const { t, locale } = useI18n();

// Initialize Firebase reset password composable
const { resetPassword, error: resetError, isPending, success: resetSuccess } = useResetPassword();

const email = ref("");
const error = ref("");
const success = ref("");
const isErrorEmail = ref(false);

// Language Selection Logic
const languages = [
  { code: "en", name: "English", flag: usaFlag },
  { code: "km", name: "Khmer", flag: khFlag },
];
const selectedLang = ref(locale.value);
const showLanguageMenu = ref(false);
const languageDropdown = ref(null);

const onLanguageChange = (code) => {
  locale.value = code;
  selectedLang.value = code;
  localStorage.setItem("locale", code);
  showLanguageMenu.value = false;
};

const toggleLanguageMenu = () => {
  showLanguageMenu.value = !showLanguageMenu.value;
};

const handleClickOutside = (event) => {
  if (languageDropdown.value && !languageDropdown.value.contains(event.target)) {
    showLanguageMenu.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});




watch(email,async (newV)=>{
  if(newV){
  await getCollectionQuery('users',[where("email", "==", newV)],(data)=>{
    if(data.length > 0){
   isErrorEmail.value = false
    error.value = ""
    }else{
      isErrorEmail.value = true
    error.value = t("Your email is not exist in our system!")
    }

  },null,false)

  }

})

const handleReset = async () => {
  error.value = "";
  success.value = "";
  
  if (!email.value) {
    error.value = t("Email is required");
    return;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.value)) {
    error.value = t("Please enter a valid email address");
    return;
  }


  if(isErrorEmail.value){
    return;
  }

  // Call Firebase reset password function
  const result = await resetPassword(email.value);
  
  if (result.success) {
    success.value = t("Reset link sent! Please check your email.");
    email.value = "";
  } else {
    error.value = resetError.value || t("Failed to send reset link");
  }
};
</script>

<style>
/* Custom animations for Tailwind */
@keyframes gradient {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes bounce-slow {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@tailwind utilities;

@layer utilities {
  .animate-gradient {
    animation: gradient 15s ease infinite;
  }

  .animate-fade-in-up {
    animation: fade-in-up 0.6s ease-out;
  }

  .animate-bounce-slow {
    animation: bounce-slow 2s ease-in-out infinite;
  }
}
</style>